import { Injectable, isDevMode, NgModule } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';
import { AppComponent } from './app.component';
import {
  API_DEFAULT_OPTIONS,
  ApiAuthModule,
  ApiModule,
  ApiTravelModule,
} from '@app/api';
import { AppRoutingModule } from './app-routing.module';
import { AppModalModule, AppModalService } from '@app/modal';
import { MatDialogModule } from '@angular/material/dialog';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule,
} from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { InputUploaderModule } from '@spd/input-uploader';
import { AppLayoutDefaultModule } from './layout/default';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  ServiceWorkerModule,
  SwUpdate,
  VersionReadyEvent,
} from '@angular/service-worker';
import { AppNotFoundComponent } from './section/not-found';
import { environment } from '../environments/environment';
import {
  LuxonDateAdapter,
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  MAT_LUXON_DATE_FORMATS,
} from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDrawerContainer } from '@angular/material/sidenav';
import { filter } from 'rxjs/operators';
import { AngularFireModule } from '@angular/fire/compat';
import {
  AngularFireMessaging,
  AngularFireMessagingModule,
} from '@angular/fire/compat/messaging';
import { AppMaintenanceComponent } from './section/maintenance';
import { MicroSentryModule } from '@micro-sentry/angular';

@Injectable({ providedIn: 'root' })
export class MessagingService {
  constructor(private afMessaging: AngularFireMessaging) {
    this.listen();
  }

  listen() {
    this.afMessaging.messages.subscribe((payload: any) => {
      const { title, body } = payload.notification || payload.data;
      this.showNotification(title, body, payload.data);
    });
  }

  private showNotification(title: string, body: string, data: any) {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/assets/icons/icon-72x72.png',
        data,
      });

      if (data.navigationId && data.travelId) {
        let url = 'https://client.grooupee.fr/travel/' + data.travelId;
        if (data.navigationId === 'chat') {
          url += '/chat';
        }
        notification.onclick = () => {
          window.open(url, '_blank');
        };
      }
    }
  }
}

const sentryModule = environment.production
  ? [
      MicroSentryModule.forRoot({
        dsn: 'https://449214d159ec449c8a7fe43db2dc3724@glitchtip.supa-dev.com/2',
      }),
    ]
  : [];

@NgModule({
  declarations: [AppComponent, AppNotFoundComponent, AppMaintenanceComponent],
  imports: [
    BrowserModule,
    ApiModule.forRoot(),
    ApiTravelModule.forRoot(),
    ApiAuthModule.forRoot(),
    AppModalModule,
    MatDialogModule,
    MatSnackBarModule,
    MatButtonModule,
    MatIconModule,
    InputUploaderModule.forRoot(),
    AppLayoutDefaultModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AngularFireModule.initializeApp({
      apiKey: 'AIzaSyB-hfmIbgppdKplWPGLIsVahgxl121Y2eY',
      authDomain: 'grooupee.firebaseapp.com',
      projectId: 'grooupee',
      storageBucket: 'grooupee.firebasestorage.app',
      messagingSenderId: '648939689065',
      appId: '1:648939689065:web:0ad562cfe8aa4f9a913ef8',
      measurementId: 'G-EQS4YWQYW8',
    }),
    AngularFireMessagingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatDrawerContainer,
    ...sentryModule,
  ],
  providers: [
    provideClientHydration(),
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 2500 },
    },
    { provide: MAT_DATE_LOCALE, useValue: 'default' },
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_LUXON_DATE_FORMATS,
      useValue: {
        parse: { dateInput: 'dd/MM/yyyy HH:mm' },
        display: { dateInput: 'dd/MM/yyyy HH:mm' },
      },
    },
    {
      provide: API_DEFAULT_OPTIONS,
      useValue: environment,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private _swUpdate: SwUpdate,
    private _appModal: AppModalService,
    private messagingService: MessagingService,
  ) {
    if (this._swUpdate.isEnabled) {
      console.log('Service worker is enabled.');

      this._swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY',
          ),
        )
        .subscribe(() => {
          document.location.reload();
        });

      this._swUpdate.checkForUpdate();
    } else {
      console.log('Service worker is disabled.');
    }
  }
}
